import { GraphqlSubscriptionResult } from "@aws-amplify/api-graphql";
import { useEffect, useMemo, useRef } from "react";
import { graphqlClient } from "~/lib/graphql-client";

interface SubscriptionObject {
  unsubscribe: () => void;
}

export interface SubscriptionQueryConfig<
  TData = unknown,
  TTransformed = TData,
> {
  query: string;
  variables?: Record<string, unknown>;
  transform?: (data: TData) => TTransformed;
  key: string;
}

export interface UseSubscriptionsConfig<TData = unknown, TTransformed = TData> {
  queries: SubscriptionQueryConfig<TData, TTransformed>[];
  onUpdate: (data: TTransformed) => void;
  shouldUpdate?: () => boolean;
  onError?: (error: Error, queryKey: string) => void;
}

type SubscriptionResponse = {
  unsubscribe: () => void;
};

interface SubscriptionResult<TData = unknown> {
  data: TData;
  errors?: unknown[];
}

/**
 * Default shouldUpdate function - defined at module scope to maintain a stable reference.
 * This prevents unnecessary re-subscriptions when the callback is used as a useEffect dependency.
 */
const defaultShouldUpdate = () => true;

/**
 * Custom hook for managing multiple GraphQL subscriptions with automatic lifecycle management.
 *
 * Key features:
 * - Automatic subscription cleanup on unmount
 * - Intelligent re-subscription only when queries change
 * - Staggered subscription creation to prevent API throttling
 * - Health monitoring to detect and recover from dead subscriptions
 * - Stable dependency management to prevent unnecessary re-renders
 *
 * Performance optimizations:
 * - Uses refs to store queries and prevent re-subscription on every render
 * - Generates stable query keys using JSON.stringify for comparison
 * - Stores handlers in refs to access latest values without triggering effects
 * - Only re-subscribes when queries actually change or subscriptions become unhealthy
 *
 * @param queries - Array of subscription configurations with GraphQL queries
 * @param onUpdate - Callback invoked when any subscription receives data
 * @param shouldUpdate - Optional function to determine if updates should be processed
 * @param onError - Optional error handler for subscription failures
 *
 * @example
 * ```tsx
 * useSubscriptions({
 *   queries: [
 *     {
 *       key: 'onUpdateUser',
 *       query: onUpdateUserSubscription,
 *       variables: { userId },
 *       transform: (data) => data.onUpdateUser
 *     }
 *   ],
 *   onUpdate: (user) => setUser(user)
 * });
 * ```
 */
export const useSubscriptions = <TData = unknown, TTransformed = TData>({
  queries,
  onUpdate,
  shouldUpdate = defaultShouldUpdate,
  onError,
}: UseSubscriptionsConfig<TData, TTransformed>) => {
  // Active subscription references for cleanup
  const subscriptionRefs = useRef<{
    [key: string]: SubscriptionResponse;
  }>({});

  // Timeout references for staggered subscription creation
  const timeoutRefs = useRef<{
    [key: string]: NodeJS.Timeout;
  }>({});

  // Track component mount state to prevent updates after unmount
  const mountedRef = useRef(true);

  // Track previous query configuration to detect changes
  const prevQueriesRef = useRef<string>("");

  /**
   * Track last successful update timestamp for each subscription.
   * Used to detect "dead" subscriptions that haven't received data in a long time.
   */
  const lastUpdateTimeRef = useRef<{ [key: string]: number }>({});

  /**
   * Store queries in ref to access the latest version without triggering re-subscription.
   * This is crucial for preventing infinite re-subscription loops in React's useEffect.
   */
  const queriesRef = useRef(queries);
  queriesRef.current = queries;

  /**
   * Store callbacks in refs to access the latest version without triggering re-subscription.
   * This prevents re-creating subscriptions when callback references change on every render.
   */
  const onUpdateRef = useRef(onUpdate);
  const onErrorRef = useRef(onError);
  const shouldUpdateRef = useRef(shouldUpdate);

  // Update refs on every render to always use the latest callback versions
  onUpdateRef.current = onUpdate;
  onErrorRef.current = onError;
  shouldUpdateRef.current = shouldUpdate;

  /**
   * Create stable key for queries array to use in useEffect dependencies.
   * Uses JSON.stringify to generate a deterministic string representation
   * that only changes when the actual query configurations change.
   */
  const queriesKey = useMemo(
    () =>
      JSON.stringify(
        queries.map((q) => ({ key: q.key, variables: q.variables })),
      ),
    [queries],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: pre-existing; deps reviewed, auto-fix risks behavior change
  useEffect(() => {
    mountedRef.current = true;

    /**
     * Health check timeout for subscriptions (30 minutes).
     * If a subscription hasn't received updates within this timeframe,
     * it's considered potentially "dead" and will be recreated on next render.
     */
    const SUBSCRIPTION_HEALTH_TIMEOUT = 30 * 60 * 1000;

    /**
     * Check if queries have actually changed by comparing serialized configurations.
     * This prevents unnecessary re-subscriptions when query objects are recreated
     * but contain the same data.
     */
    const currentQueriesString = JSON.stringify(
      queriesRef.current.map((q) => ({ key: q.key, variables: q.variables })),
    );
    const queriesChanged = currentQueriesString !== prevQueriesRef.current;

    /**
     * Optimization: Skip re-subscription if queries haven't changed and
     * all expected subscriptions exist and are healthy.
     */
    if (!queriesChanged && Object.keys(subscriptionRefs.current).length > 0) {
      const expectedKeys = queriesRef.current.map((q) => q.key);
      const existingKeys = Object.keys(subscriptionRefs.current);
      const allSubscriptionsExist = expectedKeys.every((key) =>
        existingKeys.includes(key),
      );

      if (allSubscriptionsExist) {
        // Check subscription health - detect stale subscriptions
        const now = Date.now();
        const hasStaleSubscription = expectedKeys.some((key) => {
          const lastUpdate = lastUpdateTimeRef.current[key];
          // Only check health if subscription has been initialized and received at least one update
          return (
            lastUpdate !== undefined &&
            now - lastUpdate > SUBSCRIPTION_HEALTH_TIMEOUT
          );
        });

        if (!hasStaleSubscription) {
          // All subscriptions are healthy, skip re-subscription
          return;
        }
      }
    }

    /**
     * Error handler with optional custom error callback.
     * Logs errors to console and delegates to custom handler if provided.
     * Uses onErrorRef to get the latest error handler without causing re-subscriptions.
     */
    const handleError = (error: Error, queryKey: string) => {
      console.error(
        `[useSubscriptions] Subscription error for ${queryKey}:`,
        error,
      );
      if (onErrorRef.current) {
        onErrorRef.current(error, queryKey);
      }
    };

    /**
     * Cleanup a single subscription by key.
     * Removes subscription, clears timeout, and cleans up tracking data.
     */
    const cleanupSubscription = (key: string) => {
      if (subscriptionRefs.current[key]) {
        subscriptionRefs.current[key].unsubscribe();
        delete subscriptionRefs.current[key];
      }
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]);
        delete timeoutRefs.current[key];
      }
      if (lastUpdateTimeRef.current[key]) {
        delete lastUpdateTimeRef.current[key];
      }
    };

    /**
     * Cleanup all active subscriptions.
     * Called when component unmounts or before re-subscribing.
     */
    const cleanupAllSubscriptions = () => {
      Object.keys(subscriptionRefs.current).forEach(cleanupSubscription);
    };

    /**
     * Create and register a new GraphQL subscription.
     * Handles subscription lifecycle, data transformation, and error handling.
     */
    const createSubscription = (
      queryConfig: SubscriptionQueryConfig<TData, TTransformed>,
    ) => {
      if (!mountedRef.current) {
        return {
          unsubscribe: () => {
            /* no-op */
          },
        };
      }

      // Clean up any existing subscription with the same key
      cleanupSubscription(queryConfig.key);

      try {
        // Initialize last update timestamp
        lastUpdateTimeRef.current[queryConfig.key] = Date.now();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const sub = (
          graphqlClient.graphql({
            query: queryConfig.query,
            variables: queryConfig.variables,
          }) as unknown as GraphqlSubscriptionResult<TData>
        ).subscribe({
          next: (result: SubscriptionResult<TData>) => {
            // Check if we should process this update using shouldUpdateRef
            if (!mountedRef.current || !shouldUpdateRef.current()) {
              return;
            }

            // Update health tracking timestamp
            lastUpdateTimeRef.current[queryConfig.key] = Date.now();

            // Transform and process the data
            const data = queryConfig.transform
              ? queryConfig.transform(result.data)
              : (result.data as unknown as TTransformed);

            if (data) {
              // Use onUpdateRef to get the latest callback
              onUpdateRef.current(data);
            }
          },
          error: (error: Error) => handleError(error, queryConfig.key),
        }) as SubscriptionObject;

        return { unsubscribe: () => sub.unsubscribe() };
      } catch (error) {
        handleError(error as Error, queryConfig.key);
        return {
          unsubscribe: () => {
            /* no-op */
          },
        };
      }
    };

    // Clean up all existing subscriptions before creating new ones
    cleanupAllSubscriptions();

    // Development-mode logging to detect subscription leaks
    if (process.env.NODE_ENV === "development") {
      console.log(
        `[useSubscriptions] Creating ${queriesRef.current.length} subscriptions`,
      );
    }

    /**
     * Create subscriptions with staggered timing to prevent API throttling.
     * Each subscription is delayed by 100ms * its index.
     */
    queriesRef.current.forEach((queryConfig, index) => {
      if (!mountedRef.current) return;

      const timeout = setTimeout(() => {
        if (mountedRef.current) {
          subscriptionRefs.current[queryConfig.key] =
            createSubscription(queryConfig);
        }
      }, index * 100);

      timeoutRefs.current[queryConfig.key] = timeout;
    });

    // Update query tracking after successful setup
    prevQueriesRef.current = currentQueriesString;

    // Cleanup function - called when component unmounts or dependencies change
    return () => {
      mountedRef.current = false;
      cleanupAllSubscriptions();
    };
    // Only depend on queriesKey - callbacks are accessed via refs
    // This prevents re-subscription on every render when callback references change
  }, [queriesKey]);

  return subscriptionRefs.current;
};
